import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Toast from '../common/Toast';
import ConnectionItem from './ConnectionItem';
import SyncCalendarForm from '../calendar/SyncCalendarForm';
import CalendarEvents from '../calendar/CalendarEvents';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  show: boolean;
}

function Connections() {
  const [connections, setConnections] = useState({
    googleCalendar: false,
    twitter: false,
    facebook: false,
  });
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'info', show: false });
  const [isSyncing, setIsSyncing] = useState(false);

  const showToast = (message: string, type: ToastState['type']) => {
    setToast({ message, type, show: true });
    setTimeout(() => setToast({ message: '', type: 'info', show: false }), 3000);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connectionStatus = urlParams.get('connection_status');
    if (connectionStatus === 'success') {
      showToast('Successfully connected to Google Calendar', 'success');
      fetchConnections();
      triggerInitialSync();
    } else if (connectionStatus === 'error') {
      showToast('Failed to connect to Google Calendar', 'error');
    } else {
      fetchConnections();
    }
  }, []);

  const triggerInitialSync = async () => {
    // Mark today as synced so Life ROI doesn't trigger a duplicate sync
    localStorage.setItem('lastCalendarSync', new Date().toDateString());

    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    const startDate = tenYearsAgo.toISOString().split('T')[0];
    const endDate = new Date().toISOString().split('T')[0];

    setIsSyncing(true);
    try {
      const response = await axios.post('/api/calendar/sync', {
        start_date: startDate,
        end_date: endDate,
        use_include_regex: false,
        use_exclude_regex: false
      });
      showToast(`Calendar synced! ${response.data.events_count} events imported.`, 'success');
    } catch (error: any) {
      console.error('Error during initial sync:', error);
      showToast('Initial sync failed: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get('/api/user/connections');
      setConnections(response.data);
    } catch (error) {
      console.error('Error fetching connections:', error);
      showToast('Failed to fetch connections', 'error');
    }
  };

  const handleConnect = async (service) => {
    try {
      const response = await axios.get(`/api/connect/${service}`);
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error(`Error connecting to ${service}:`, error);
      showToast(`Failed to connect to ${service}`, 'error');
    }
  };
  const handleDisconnect = async (service) => {
    try {
      await axios.post(`/api/disconnect/${service}`);
      fetchConnections();
      showToast(`Successfully disconnected from ${service}`, 'success');
    } catch (error) {
      console.error(`Error disconnecting from ${service}:`, error);
      showToast(`Failed to disconnect from ${service}`, 'error');
    }
  };

  const handleSyncCalendar = async (syncOptions: any) => {
    try {
      const response = await axios.post('/api/calendar/sync', syncOptions);
      showToast(`Calendar synced successfully (${response.data.events_count} events)`, 'success');
    } catch (error: any) {
      console.error('Error syncing calendar:', error);
      showToast('Failed to sync calendar: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center mb-6">
          <Link 
            to="/life-roi" 
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mr-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-1" />
            Back to Life ROI
          </Link>
        </div>
        <h1 className="text-3xl font-bold mb-8">Calendar Setup</h1>
        <p className="text-gray-600 mb-6">
          Connect your Google Calendar to analyze your time allocation and get insights into your productivity patterns.
        </p>
        <div className="space-y-6">
          <ConnectionItem
            name="Google Calendar"
            connected={connections.googleCalendar}
            onConnect={() => handleConnect('google-calendar')}
            onDisconnect={() => handleDisconnect('google-calendar')}
          />
        </div>
      </div>
      
      {/* Initial sync progress banner */}
      {isSyncing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
          <div>
            <p className="font-medium text-blue-800">Syncing your calendar...</p>
            <p className="text-sm text-blue-600">Importing up to 10 years of events. This may take a minute.</p>
          </div>
        </div>
      )}

      {/* Google Calendar Sync Section */}
      {connections.googleCalendar && !isSyncing && (
        <div className="border-t pt-8">
          <SyncCalendarForm onSync={handleSyncCalendar} />
          <CalendarEvents onEventsUpdated={() => { /* Events updated */ }} />
        </div>
      )}
      
      {/* Setup Guide */}
      {!connections.googleCalendar && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Getting Started</h3>
          <div className="text-blue-700 space-y-2">
            <p>1. Click "Connect" above to authorize access to your Google Calendar</p>
            <p>2. Once connected, use the sync options to import your events</p>
            <p>3. Return to Life ROI to view your time allocation insights</p>
          </div>
        </div>
      )}
      
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}

export default Connections;